import "@/client/libs/dayjs";

import { i18n } from "@lingui/core";
import { detect, fromStorage, fromUrl } from "@lingui/detect-locale";
import { I18nProvider } from "@lingui/react";
import { languages } from "@reactive-resume/utils";
import { useEffect, useState } from "react";

import { LoadingPage } from "../pages/loading/page";
import { defaultLocale, dynamicActivate } from "../libs/lingui";
import { updateUser } from "../services/user";
import { useAuthStore } from "../stores/auth";

type Props = {
  children: React.ReactNode;
};

const getUserLocale = () => {
  const browserLocale = navigator.language;
  return languages.find(({ id, locale }) => [id, locale].includes(browserLocale))?.locale
    ?? defaultLocale;
};

export const LocaleProvider = ({ children }: Props) => {
  const browserLanguage = getUserLocale();
  const userLocale = useAuthStore((state) => state.user?.locale ?? browserLanguage);
  const [ isReady, setIsReady ] = useState(false);

  useEffect(() => {
    const detectedLocale = languages.find(
      lang => lang.locale === detect(fromUrl("locale"), fromStorage("locale"), userLocale)
    )?.locale ?? defaultLocale;

    dynamicActivate(detectedLocale).then(() =>  setIsReady(true));
  }, [userLocale]);

  if (!isReady) {
    return <LoadingPage />;
  }

  return <I18nProvider i18n={i18n}>{children}</I18nProvider>;
};

export const changeLanguage = async (locale: string) => {
  // Update locale in local storage
  window.localStorage.setItem("locale", locale);

  // Update locale in user profile, if authenticated
  const state = useAuthStore.getState();
  if (state.user) await updateUser({ locale }).catch(() => null);

  // Reload the page for language switch to take effect
  window.location.reload();
};
